import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Search, Pencil, Trash2, FileText, Trash, Eye, Truck, MessageSquare } from "lucide-react";
import { api, apiError, fmtDate } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { hasPerm } from "../lib/permissions";

const STATUSES = ["Pending", "In Transit", "Delivered", "Cancelled"];
const STATUS_STYLE = {
  Pending: "bg-amber-100 text-amber-800 border-amber-200",
  "In Transit": "bg-sky-100 text-sky-800 border-sky-200",
  Delivered: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Cancelled: "bg-slate-100 text-slate-600 border-slate-200",
};

const emptyLine = () => ({ product_id: "", grade: "", batch_number: "", quantity: "", unit: "", hsn_code: "", available_stock: 0, pending: null });
const BLANK = {
  customer_id: "", customer_name: "", contact_number: "", destination_address: "",
  invoice_id: "", invoice_no: "", vehicle_number: "", transporter: "", lr_number: "",
  dispatch_date: new Date().toISOString().slice(0, 10), delivery_status: "Pending", remarks: "",
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
    {children}
  </div>
);

/** Searchable product picker driven by the Product Master (name, SKU, grade or HSN). */
function ProductPicker({ products, value, onChange, testid, disabled }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const selected = products.find((p) => p.id === value);
  const list = useMemo(() => {
    const n = q.trim().toLowerCase();
    const base = n ? products.filter((p) => `${p.product_name} ${p.product_code} ${p.grade} ${p.hsn_code}`.toLowerCase().includes(n)) : products;
    return base.slice(0, 20);
  }, [products, q]);

  return (
    <div className="relative">
      <Input
        data-testid={testid}
        disabled={disabled}
        value={open ? q : selected ? `${selected.product_name}${selected.product_code ? ` · ${selected.product_code}` : ""}` : q}
        onFocus={() => { setOpen(true); setQ(""); }}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        placeholder="Search product, SKU, grade or HSN"
        className="bg-white h-9"
      />
      {open && (
        <div className="absolute z-40 mt-1 w-[320px] max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-sm shadow-lg">
          {list.length === 0 && <div className="px-3 py-3 text-xs text-slate-400">No product matched</div>}
          {list.map((p) => (
            <button type="button" key={p.id} data-testid={`${testid}-opt-${p.id}`}
              className="w-full text-left px-3 py-2 hover:bg-slate-50"
              onMouseDown={() => { onChange(p.id); setQ(""); setOpen(false); }}>
              <div className="text-sm text-slate-800">{p.product_name}{p.product_code ? <span className="text-slate-400"> · {p.product_code}</span> : null}</div>
              <div className="text-[11px] text-slate-500">
                {[p.grade, p.hsn_code ? `HSN ${p.hsn_code}` : "", `Stock ${p.available_stock} ${p.unit}`].filter(Boolean).join(" · ")}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DispatchPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [invoiceInfo, setInvoiceInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [viewOnly, setViewOnly] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [lines, setLines] = useState([emptyLine()]);
  const [delRow, setDelRow] = useState(null);

  const canCreate = hasPerm(user, "dispatch", "create");
  const canEdit = hasPerm(user, "dispatch", "edit");
  const canDelete = hasPerm(user, "dispatch", "delete");
  const canPdf = hasPerm(user, "dispatch", "export_pdf");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, p, c] = await Promise.all([
        api.get("/dispatches"), api.get("/dispatches/products"), api.get("/customers"),
      ]);
      setRows(d.data); setProducts(p.data.products || []); setCustomers(c.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => rows.filter((r) => {
    const s = search.trim().toLowerCase();
    const hit = !s || ["dispatch_no", "customer_name", "vehicle_number", "transporter", "invoice_no", "product_name"]
      .some((k) => String(r[k] || "").toLowerCase().includes(s));
    return hit && (!statusFilter || r.delivery_status === statusFilter);
  }), [rows, search, statusFilter]);

  const prod = (id) => products.find((p) => p.id === id);

  const setLine = (i, key, value) => setLines((prev) => {
    const next = prev.map((l) => ({ ...l }));
    next[i][key] = value;
    if (key === "product_id") {
      const p = prod(value);
      next[i].grade = p?.grade || "";
      next[i].hsn_code = p?.hsn_code || "";
      next[i].unit = p?.unit || "";
      next[i].available_stock = p?.available_stock || 0;
      next[i].batch_number = "";
      const line = invoiceInfo?.lines?.find((x) => x.product_id === value);
      next[i].pending = line ? line.pending : null;
    }
    if (key === "batch_number") {
      const b = prod(next[i].product_id)?.batches?.find((x) => x.batch_number === value);
      if (b?.grade) next[i].grade = b.grade;
    }
    return next;
  });

  const loadCustomerInvoices = async (customerId) => {
    setInvoices([]); setInvoiceInfo(null);
    if (!customerId) return;
    try {
      const { data } = await api.get(`/dispatches/customer-invoices/${customerId}`);
      setInvoices(data.invoices || []);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const pickInvoice = async (invoiceId) => {
    if (!invoiceId) { setForm((f) => ({ ...f, invoice_id: "", invoice_no: "" })); setInvoiceInfo(null); return; }
    try {
      const { data } = await api.get(`/dispatches/invoice-lines/${invoiceId}`,
        { params: { exclude_dispatch_id: editing?.id || "" } });
      setInvoiceInfo(data);
      setForm((f) => ({
        ...f, invoice_id: invoiceId, invoice_no: data.invoice_no,
        destination_address: f.destination_address || data.shipping_address || "",
      }));
      const pendingLines = data.lines.filter((l) => l.pending > 0);
      if (pendingLines.length === 0) {
        toast.info(`Invoice ${data.invoice_no} is already fully dispatched`);
        return;
      }
      setLines(pendingLines.map((l) => ({
        product_id: l.product_id, grade: l.grade, batch_number: l.batch_number || "",
        quantity: l.pending, unit: l.unit, hsn_code: l.hsn_code,
        available_stock: l.available_stock, pending: l.pending,
      })));
      toast.success(`Loaded ${pendingLines.length} pending line(s) from ${data.invoice_no}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openNew = () => {
    setEditing(null); setViewOnly(false); setForm(BLANK); setLines([emptyLine()]);
    setInvoices([]); setInvoiceInfo(null); setOpen(true);
  };

  const openRow = async (row, readOnly) => {
    setEditing(row); setViewOnly(readOnly);
    setForm({ ...BLANK, ...row, dispatch_date: String(row.dispatch_date || "").slice(0, 10) });
    const items = row.items && row.items.length ? row.items : (row.product_name ? [{
      product_id: "", grade: row.grade || "", batch_number: row.batch_number || "",
      quantity: row.quantity || "", unit: row.unit || "", hsn_code: "",
    }] : [emptyLine()]);
    setLines(items.map((it) => ({
      ...emptyLine(), ...it,
      available_stock: prod(it.product_id)?.available_stock ?? it.available_stock ?? 0,
    })));
    setOpen(true);
    if (row.customer_id) await loadCustomerInvoices(row.customer_id);
    if (row.invoice_id) {
      try {
        const { data } = await api.get(`/dispatches/invoice-lines/${row.invoice_id}`, { params: { exclude_dispatch_id: row.id } });
        setInvoiceInfo(data);
        setLines((prev) => prev.map((l) => {
          const il = data.lines.find((x) => x.product_id === l.product_id);
          return il ? { ...l, pending: il.pending } : l;
        }));
      } catch { /* invoice may have been removed */ }
    }
  };

  const isLegacy = !!(editing && !(editing.items || []).length && editing.product_name);

  const save = async () => {
    const payload = {
      ...form,
      items: lines.filter((l) => l.product_id && Number(l.quantity) > 0).map((l) => ({
        product_id: l.product_id, grade: l.grade, batch_number: l.batch_number,
        quantity: Number(l.quantity), unit: l.unit,
      })),
    };
    if (payload.items.length === 0 && !isLegacy) { toast.error("Add at least one product with a dispatch quantity"); return; }
    try {
      if (editing) await api.put(`/dispatches/${editing.id}`, payload);
      else await api.post("/dispatches", payload);
      toast.success(editing ? "Dispatch updated" : "Dispatch created");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async () => {
    try { await api.delete(`/dispatches/${delRow.id}`); toast.success("Dispatch deleted"); setDelRow(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openPdf = (id) => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/pdf/dispatch/${id}?token=${localStorage.getItem("token")}`, "_blank");

  const sendWhatsapp = async (id) => {
    try {
      const { data } = await api.post("/whatsapp/send", { doctype: "dispatch", item_id: id });
      if (data.sent) { toast.success(`Dispatch note sent on WhatsApp to +${data.mobile}`); return; }
      if (data.whatsapp_link) window.open(data.whatsapp_link, "_blank");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const overQty = (l) => {
    const qty = Number(l.quantity);
    if (!(qty > 0)) return false;
    if (isLegacy && !l.product_id) return false;
    const invoiceLinked = l.pending !== null && l.pending !== undefined;
    // an invoice-linked line is capped by the pending quantity only — the invoice already took it out of stock
    if (invoiceLinked) return qty - Number(l.pending) > 0.001;
    return qty - Number(l.available_stock) > 0.001;
  };

  return (
    <div data-testid="dispatch-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-400 font-semibold">
            <Truck className="w-3.5 h-3.5" /> Operations
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-head tracking-tight mt-1">Dispatch</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            Products come from the Product Master with live available stock. Stock is deducted by the Sales Invoice — a dispatch note never deducts it again.
          </p>
        </div>
        {canCreate && <Button data-testid="add-dispatch-btn" className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5" onClick={openNew}><Plus className="w-4 h-4" /> New Dispatch</Button>}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
          <Input data-testid="dispatch-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search dispatch no, customer, vehicle, transporter or invoice" className="bg-white h-9 pl-9" />
        </div>
        <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="dispatch-status-filter" className="bg-white h-9 sm:w-56"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="dispatch-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>{["Dispatch No", "Date", "Customer", "Products", "Total Qty", "Invoice", "Vehicle", "Transporter", "Status", "Actions"].map((h) => (
              <th key={h} className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</th>))}</tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400">Loading...</td></tr>
              : filtered.length === 0 ? <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400" data-testid="dispatch-empty">No dispatch records found</td></tr>
                : filtered.map((r) => (
                  <tr key={r.id} data-testid={`dispatch-row-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2.5 whitespace-nowrap font-mono text-xs text-slate-700">{r.dispatch_no || r.id.slice(0, 8)}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-slate-600">{fmtDate(r.dispatch_date)}</td>
                    <td className="px-3 py-2.5 text-slate-800 whitespace-nowrap">{r.customer_name}</td>
                    <td className="px-3 py-2.5 text-slate-600">
                      {(r.items && r.items.length) ? (
                        <span data-testid={`dispatch-items-${r.id}`}>
                          {r.items.map((it) => `${it.product_name}${it.grade ? ` (${it.grade})` : ""} — ${it.quantity} ${it.unit || ""}`).join(", ")}
                        </span>
                      ) : <span className="text-slate-500">{r.product_name || "-"}</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums">{Number(r.total_quantity ?? r.quantity ?? 0).toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{r.invoice_no || "-"}</td>
                    <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{r.vehicle_number || "-"}</td>
                    <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{r.transporter || "-"}</td>
                    <td className="px-3 py-2.5"><Badge variant="outline" className={`rounded-full px-2 text-[11px] ${STATUS_STYLE[r.delivery_status] || ""}`}>{r.delivery_status || "Pending"}</Badge></td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-0.5">
                        {canEdit
                          ? <Button data-testid={`edit-dispatch-${r.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-[#0B132B]" title="Edit" onClick={() => openRow(r, false)}><Pencil className="w-4 h-4" /></Button>
                          : <Button data-testid={`view-dispatch-${r.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500" title="View" onClick={() => openRow(r, true)}><Eye className="w-4 h-4" /></Button>}
                        {canPdf && <Button data-testid={`pdf-dispatch-${r.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-[#0B132B]" title="Dispatch Note PDF" onClick={() => openPdf(r.id)}><FileText className="w-4 h-4" /></Button>}
                        {canPdf && <Button data-testid={`wa-dispatch-${r.id}`} size="icon" variant="ghost" className="h-8 w-8 text-emerald-600 hover:text-emerald-700" title="Send dispatch note on WhatsApp" onClick={() => sendWhatsapp(r.id)}><MessageSquare className="w-4 h-4" /></Button>}
                        {canDelete && <Button data-testid={`delete-dispatch-${r.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" title="Delete" onClick={() => setDelRow(r)}><Trash2 className="w-4 h-4" /></Button>}
                      </div>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="dispatch-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">{viewOnly ? "Dispatch details" : editing ? `Edit dispatch ${editing.dispatch_no || ""}` : "New dispatch"}</DialogTitle>
            <DialogDescription>Pick the customer, optionally an invoice to pull pending quantities, then the products from the Product Master.</DialogDescription>
          </DialogHeader>

          <fieldset disabled={viewOnly} className="space-y-4 min-w-0 w-full">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Customer">
                <Select value={form.customer_id} onValueChange={(v) => {
                  const c = customers.find((x) => x.id === v);
                  setForm({ ...form, customer_id: v, customer_name: c?.company_name || "", contact_number: c?.mobile || form.contact_number, destination_address: c?.shipping_address || form.destination_address, invoice_id: "", invoice_no: "" });
                  loadCustomerInvoices(v);
                }}>
                  <SelectTrigger data-testid="dispatch-customer" className="bg-white h-9"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent className="max-h-72">{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Sales Invoice (optional)">
                <Select value={form.invoice_id || "none"} onValueChange={(v) => pickInvoice(v === "none" ? "" : v)} disabled={!form.customer_id}>
                  <SelectTrigger data-testid="dispatch-invoice" className="bg-white h-9"><SelectValue placeholder="No invoice" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="none">No invoice (direct dispatch)</SelectItem>
                    {invoices.map((i) => (
                      <SelectItem key={i.id} value={i.id}>
                        {i.invoice_no} · {i.invoice_date} {i.fully_dispatched ? "· fully dispatched" : `· ${i.total_pending} pending`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Contact Number"><Input data-testid="dispatch-contact" value={form.contact_number || ""} onChange={(e) => setForm({ ...form, contact_number: e.target.value })} className="bg-white h-9" /></Field>
              <div className="sm:col-span-3"><Field label="Delivery Address"><Input data-testid="dispatch-address" value={form.destination_address || ""} onChange={(e) => setForm({ ...form, destination_address: e.target.value })} className="bg-white h-9" /></Field></div>
            </div>

            {invoiceInfo && (
              <div className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 text-xs text-slate-600" data-testid="invoice-summary">
                Invoice <b>{invoiceInfo.invoice_no}</b> · invoiced {invoiceInfo.total_invoiced} · already dispatched {invoiceInfo.total_dispatched} · <b>pending {invoiceInfo.total_pending}</b>
                {invoiceInfo.fully_dispatched && <span className="text-emerald-700"> · fully dispatched</span>}
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-slate-800">Products</h3>
                {!viewOnly && <Button data-testid="add-product-line" type="button" size="sm" variant="outline" className="rounded-sm gap-1.5" onClick={() => setLines([...lines, emptyLine()])}><Plus className="w-3.5 h-3.5" /> Add Product</Button>}
              </div>
              <div className="overflow-x-auto border border-slate-200 rounded-sm">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>{["Product", "Grade", "HSN", "Batch", "Available Stock", "Quantity", "Unit", ""].map((h) => (
                      <th key={h} className="px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</th>))}</tr>
                  </thead>
                  <tbody>
                    {lines.map((l, i) => {
                      const p = prod(l.product_id);
                      return (
                        <tr key={i} className="border-b border-slate-100 align-top" data-testid={`dispatch-line-${i}`}>
                          <td className="px-2 py-2 min-w-[240px]">
                            {isLegacy && !l.product_id ? (
                              <div className="text-sm text-slate-700 py-2" data-testid={`legacy-product-${i}`}>
                                {editing.product_name}
                                <span className="block text-[11px] text-slate-400">Legacy record — product was typed manually</span>
                              </div>
                            ) : (
                              <ProductPicker products={products} value={l.product_id} testid={`line-product-${i}`}
                                disabled={viewOnly} onChange={(v) => setLine(i, "product_id", v)} />
                            )}
                            {p?.product_code && <div className="text-[11px] text-slate-400 mt-0.5" data-testid={`line-code-${i}`}>SKU: {p.product_code}</div>}
                          </td>
                          <td className="px-2 py-2 min-w-[130px]">
                            <Input data-testid={`line-grade-${i}`} value={l.grade || ""} onChange={(e) => setLine(i, "grade", e.target.value)} className="bg-white h-9" />
                          </td>
                          <td className="px-2 py-2 text-slate-600 whitespace-nowrap" data-testid={`line-hsn-${i}`}>{l.hsn_code || "-"}</td>
                          <td className="px-2 py-2 min-w-[150px]">
                            {p?.batches?.length ? (
                              <Select value={l.batch_number || "none"} onValueChange={(v) => setLine(i, "batch_number", v === "none" ? "" : v)}>
                                <SelectTrigger data-testid={`line-batch-${i}`} className="bg-white h-9"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">No batch</SelectItem>
                                  {p.batches.map((b) => <SelectItem key={b.batch_number} value={b.batch_number}>{b.batch_number} ({b.stock})</SelectItem>)}
                                </SelectContent>
                              </Select>
                            ) : <Input data-testid={`line-batch-${i}`} value={l.batch_number || ""} onChange={(e) => setLine(i, "batch_number", e.target.value)} className="bg-white h-9" />}
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap font-mono tabular-nums text-slate-700" data-testid={`line-stock-${i}`}>
                            {l.product_id ? `${Number(l.available_stock).toLocaleString("en-IN")} ${l.unit || ""}` : "-"}
                            {l.pending !== null && l.pending !== undefined && <span className="block text-[11px] text-slate-400">pending {l.pending}</span>}
                          </td>
                          <td className="px-2 py-2 min-w-[120px]">
                            <Input data-testid={`line-qty-${i}`} type="number" min="0" step="0.001" value={l.quantity}
                              onChange={(e) => setLine(i, "quantity", e.target.value)}
                              className={`bg-white h-9 text-right ${overQty(l) ? "border-red-400" : ""}`} />
                            {overQty(l) && <div className="text-[11px] text-red-600 mt-0.5" data-testid={`line-error-${i}`}>Exceeds available / pending quantity</div>}
                          </td>
                          <td className="px-2 py-2 text-slate-600 whitespace-nowrap" data-testid={`line-unit-${i}`}>{l.unit || "-"}</td>
                          <td className="px-2 py-2 text-right">
                            {!viewOnly && lines.length > 1 && (
                              <Button data-testid={`remove-line-${i}`} type="button" size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600"
                                onClick={() => setLines(lines.filter((_, x) => x !== i))}><Trash className="w-4 h-4" /></Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="text-right text-sm text-slate-700 mt-2" data-testid="dispatch-total-qty">
                Total dispatch quantity: <b className="font-mono tabular-nums">{lines.reduce((s, l) => s + (Number(l.quantity) || 0), 0).toLocaleString("en-IN")}</b>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Vehicle Number"><Input data-testid="dispatch-vehicle" value={form.vehicle_number || ""} onChange={(e) => setForm({ ...form, vehicle_number: e.target.value })} className="bg-white h-9" /></Field>
              <Field label="Transporter"><Input data-testid="dispatch-transporter" value={form.transporter || ""} onChange={(e) => setForm({ ...form, transporter: e.target.value })} className="bg-white h-9" /></Field>
              <Field label="LR / GR Number"><Input data-testid="dispatch-lr" value={form.lr_number || ""} onChange={(e) => setForm({ ...form, lr_number: e.target.value })} className="bg-white h-9" /></Field>
              <Field label="Dispatch Date"><Input data-testid="dispatch-date" type="date" value={String(form.dispatch_date || "").slice(0, 10)} onChange={(e) => setForm({ ...form, dispatch_date: e.target.value })} className="bg-white h-9" /></Field>
              <Field label="Delivery Status">
                <Select value={form.delivery_status || "Pending"} onValueChange={(v) => setForm({ ...form, delivery_status: v })}>
                  <SelectTrigger data-testid="dispatch-delivery-status" className="bg-white h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Remarks"><Input data-testid="dispatch-remarks" value={form.remarks || ""} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="bg-white h-9" /></Field>
            </div>
          </fieldset>

          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Close</Button>
            {!viewOnly && <Button data-testid="save-dispatch-btn" className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm" onClick={save}>{editing ? "Save changes" : "Create dispatch"}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delRow} onOpenChange={() => setDelRow(null)}>
        <AlertDialogContent data-testid="delete-dispatch-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this dispatch?</AlertDialogTitle>
            <AlertDialogDescription>
              Dispatch {delRow?.dispatch_no || ""} for {delRow?.customer_name} will be removed. Stock is not affected — it is posted by the Sales Invoice.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-dispatch" className="rounded-sm bg-red-600 hover:bg-red-700" onClick={remove}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
